import { useTranslation } from 'react-i18next';
import EmptyRender from '@/enterprise/components/EmptyRender';
import Skeleton from '@/enterprise/components/Skeleton';
import useLocaleFormat from '@/utils/useLocaleFormat';

interface HistoryStatsBarProps {
  clearedInvoiceAmount?: number;
  clearedEarn?: number;
  clearedDiscountWeightedAvg?: number | null;
  currency?: string;
  loading: boolean;
  hideDiscountStats?: boolean;
}

const HistoryStatsBar = ({
  clearedInvoiceAmount = 0,
  clearedEarn = 0,
  clearedDiscountWeightedAvg,
  currency = 'USD',
  loading,
  hideDiscountStats = false,
}: HistoryStatsBarProps) => {
  const { t } = useTranslation();
  const { asCurrency, asPercent } = useLocaleFormat();

  return (
    <div className="rounded-2xl bg-canvas">
      <div className="grid grid-cols-1 border-none p-6 lg:grid-cols-3 lg:[&>*:not(:last-child)]:border-r lg:[&>*]:border-deepGray-500/[12%]">
        <div className="mb-6 text-center lg:mb-0">
          <div className="text-sm text-text-secondary">{t('core.awardedAmount')}</div>
          <div className="text-xl font-medium">
            {loading ? (
              <Skeleton className="m-auto h-7 w-40" shadow={false} />
            ) : (
              <EmptyRender condition={!!clearedInvoiceAmount}>{asCurrency(clearedInvoiceAmount, currency)}</EmptyRender>
            )}
          </div>
        </div>
        <div className="mb-6 text-center lg:mb-0">
          <div className="text-sm text-text-secondary">{t('core.totalDiscount')}</div>
          <div className="text-xl font-medium">
            {loading ? (
              <Skeleton className="m-auto h-7 w-40" shadow={false} />
            ) : (
              <EmptyRender condition={!!clearedEarn && !hideDiscountStats}>
                {asCurrency(clearedEarn, currency)}
              </EmptyRender>
            )}
          </div>
        </div>
        <div className="mb-6 text-center lg:mb-0">
          <div className="text-sm text-text-secondary">{t('core.averageDiscount')}</div>
          <div className="text-xl font-medium">
            {loading ? (
              <Skeleton className="m-auto h-7 w-40" shadow={false} />
            ) : (
              <EmptyRender condition={!!clearedDiscountWeightedAvg && !hideDiscountStats}>
                {asPercent({ value: (clearedDiscountWeightedAvg ?? 0) / 100, decimals: 2 })}
              </EmptyRender>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HistoryStatsBar;
